import {
  Button,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import colors from '@/theme/colors';
import { cutStringOverflow, displayCapitalizedUnderscoredString } from '@/utils/stringUtils';
import SvgIcon from '../SvgIcon';
import { svgIconPaths } from '@/assets/svg/icons';

interface Props<TOption> {
  title: string;
  options: readonly TOption[];
  selectedOptions?: string[];
  onOptionClick: (option: TOption) => void;
  height?: string;
  width?: string;
  popoverWidth?: string;
  formatOption?: boolean;
}

const DropdownMultiSelect = <TOption,>({
  title,
  options,
  onOptionClick,
  height = '54px',
  width = '',
  popoverWidth = '260px',
  selectedOptions,
  formatOption = true,
}: Props<TOption>) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const noValueSelected = !selectedOptions || selectedOptions.length === 0;

  const formatLabel = (option: string) =>
    formatOption && !option.includes('+') ? displayCapitalizedUnderscoredString(option) : option;

  const selectedOptionsText = cutStringOverflow({
    str: selectedOptions?.map(formatLabel).join(', '),
    length: selectedOptions?.includes('OTHER') ? 34 : 80,
    overflowSign: '..',
  });

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement={'bottom'}>
      <PopoverTrigger>
        <Button
          minHeight={'40px'}
          height={height}
          width={width}
          bg={colors.extra.white}
          cursor={'pointer'}
          padding={0}
          border={`1px solid ${noValueSelected ? '#E2E8F0' : '#004179'}`}
          boxShadow={
            isOpen
              ? '0px 2px 8px 0px #00417933'
              : `0px 2px ${noValueSelected ? '4px 0px #00417911' : '8px 0px #00417933'}`
          }
          backgroundColor={isOpen ? 'E2E8F0' : '#FFF'}
          _hover={{ opacity: 0.7 }}
          overflow={'hidden'}
        >
          <HStack height={'100%'} width={'100%'} minWidth={0} padding={'18px'} gap={'8px'}>
            {/* minWidth:0 lets the label shrink so isTruncated can ellipsize it. */}
            <VStack flexGrow={1} minWidth={0} alignItems={'flex-start'} gap={'2px'}>
              {!noValueSelected && (
                <Text
                  fontSize={'10px'}
                  lineHeight={'10px'}
                  fontWeight={800}
                  letterSpacing={'0.3px'}
                  color={'text.mediumGray'}
                  width={'100%'}
                  textAlign={'left'}
                  isTruncated
                >
                  {title.toUpperCase()}
                </Text>
              )}
              <Text
                fontSize={noValueSelected ? '14px' : '15px'}
                color={noValueSelected ? 'text.mediumGray' : 'text.darkBlue'}
                lineHeight={'20px'}
                fontWeight={noValueSelected ? 700 : 600}
                width={'100%'}
                textAlign={'left'}
                isTruncated
              >
                {noValueSelected ? title : selectedOptionsText}
              </Text>
            </VStack>
            {isOpen ? (
              <ChevronUpIcon
                boxSize={'5'}
                flexShrink={0}
                color={noValueSelected ? colors.text.mediumGray : colors.text.darkBlue}
              />
            ) : (
              <ChevronDownIcon
                boxSize={'5'}
                flexShrink={0}
                color={noValueSelected ? colors.text.mediumGray : colors.text.darkBlue}
              />
            )}
          </HStack>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        bg="white"
        width={popoverWidth}
        maxHeight={'300px'}
        overflowY={'auto'}
        border={'1px solid #95B1C9'}
        padding={'20px'}
        paddingBottom={'15px'}
        borderRadius={'8px'}
        boxShadow={'0px 6px 12px 0px #0E006224'}
      >
        <VStack align={'stretch'} gap={'8px'}>
          {options.map((option) => {
            const isOptionSelected = selectedOptions?.includes(option as string);

            return (
              <HStack
                key={option as string}
                cursor={'pointer'}
                justifyContent={'space-between'}
                alignItems={'center'}
                width={'100%'}
                data-group
                onClick={() => onOptionClick(option)}
              >
                <Text
                  fontWeight={isOptionSelected ? 700 : 600}
                  _groupHover={{ fontWeight: 700 }}
                  noOfLines={1}
                  color={'text.darkBlue'}
                >
                  {formatLabel(option as string)}
                </Text>
                {isOptionSelected && (
                  <SvgIcon iconPath={svgIconPaths.check} color={colors.primary[500]} size={'12'} />
                )}
              </HStack>
            );
          })}
        </VStack>
      </PopoverContent>
    </Popover>
  );
};

export default DropdownMultiSelect;
